import { getAccountProvider } from 'app/lib/utils/adaya/AdayaUtils';
import AppEvents from 'lib/AppEvents';
import { DataStatus } from 'lib/data/providers/IDataProvider';
import ApiService from 'services/ApiService';
import DataService from 'services/data/DataService';
import EventService from 'services/EventService';
import IDbOrCacheStore from 'stores/IDbOrCacheStore';
import { forEachAsync } from "utils/async";
import { DATE_FORMAT, today, tzDate, yesterday } from 'utils/DateUtils';
import { findError } from 'utils/errorUtils';
import { getLogger } from 'utils/logging';

const { log, warn } = getLogger('ShopifyService', { color: 'green', enabled: false });

const sortDates = (a: string, b: string) => a.localeCompare(b);

class ShopifyService {

    private store: IDbOrCacheStore;

    constructor() {
        this.store = new IDbOrCacheStore('SHP_');
        EventService.subscribe(AppEvents.LOGIN, async () => {
            await this.store.clear();
        });
        EventService.subscribe(AppEvents.LOGOUT, async () => {
            await this.store.clear();
        });
    }

    // Helper method to pull Shopify revenue between the provided from and to dates
    private async pullShopifyRevenue(accountId: string, from: string, to: string): Promise<{ day: string, sales: number, revenue: number }[]> {
        let r = await ApiService.get(`/account-data/${accountId}/shopify/revenue`, { params: { from, to } });
        if (!r.success) {
            throw new "Error getting revenue: " + findError(r.error);
        }
        return r.revenues;
    }

    // Post a job to calculate Shopify revenue, starting with "from" date and up to but not including "to" date
    private async postShopifyRevenueJob(accountId: string, from: string, to: string, progressUpdate: any) {
        let { taskId } = await ApiService.post(`/account-data/${accountId}/shopify/revenue/job`, {
            from,
            to
        });
        await ApiService.pollForTaskCompletion(taskId, (progress) => progressUpdate(progress));
        return await this.pullShopifyRevenue(accountId, from, to);
    }

    // Exposed method for pulling in revenue data
    public async getRevenueData(
        accountId: string,
        dateArrays: { name: string, days: string[] }[],
        finishedCallback: (days: { [key: string]: number }) => any,
        updateCallback: (status: any) => any,
        progressMessage: string = '',
    ) {
        try {
            // Initialize variables
            let missingDays = {}
            let min;
            let max;
            let todayStr = today(timezone).format(DATE_FORMAT);
            let yesterdayStr = yesterday(timezone).format(DATE_FORMAT);

            // Get all the currently cached days
            let allDays = await this.store.get('allDays') || {};

            // Helper method to map the retrieved revenues to the days (deleting from missingDays in the process)
            const mapRetrievedRevenues = (revenues) => {
                revenues.forEach(r => {
                    const { day, sales, revenue, isComplete } = r;
                    let complete = isComplete ? true : false;
                    allDays[day] = { sales, revenue, isComplete: complete };

                    if (complete ) {
                        delete missingDays[day];
                    }
                });
            }

            // Helper method to wrap progress updates if running a task on the back-end
            const progressUpdate = (progress: number) => {
                if (updateCallback) {
                    updateCallback({
                        status: DataStatus.PROGRESS,
                        id: 'shopify-revenue',
                        progress: (progress || 0) + '%',
                        message: progressMessage
                    });
                }
            }

            // Audit through the date arrays to find any missing days from the cache.
            // While doing so, find the min and max of the missing days.
            await forEachAsync(dateArrays, (dateArray) => {
                dateArray.days.forEach(day => {
                    let d = allDays[day];
                    if (!d || !d.isComplete || day == todayStr || day == yesterdayStr) {
                        // Add day as a key in missingDays
                        //console.log('missing day for revenue:', day)
                        missingDays[day] = day;
                        let _day = tzDate(day);
                        if (!min) min = _day;
                        if (!max) max = _day;
                        if (min.diff(_day, 'days') > 0) min = _day;
                        if (max.diff(_day, 'days') < 0) max = _day;
                    }
                });
            });

            // If there are missing days from local storage, see if they are already in backend (without starting a job):
            if (min && max) {
                log('PULLING REVENUE', min, max)
                let revenues = await this.pullShopifyRevenue(accountId, min.format(DATE_FORMAT), max.format(DATE_FORMAT));
                mapRetrievedRevenues(revenues);
            }

            // If there are still missing days, post a job to retrieve them
            let _missingDays = Object.keys(missingDays);

            if (_missingDays.length > 0) {
                //console.log('Missing days, updating...', _missingDays)
                // Sort missing days to set the new range ("from" up to but not including "to")
                _missingDays.sort(sortDates);
                let from = _missingDays[0];
                let to = tzDate(_missingDays[_missingDays.length - 1]).add(1, 'd').format(DATE_FORMAT);
                log('POSTING REVENUE JOB', from, to);
                let revenues = await this.postShopifyRevenueJob(accountId, from, to, progressUpdate);
                //console.log('Post shopify revenue job result:', revenues)
                mapRetrievedRevenues(revenues);s
            }

            // Update the cache
            await this.store.set('allDays', allDays);

            // Finally, call the callback with the final results;
            await finishedCallback(allDays);
        } catch(e) {
            log(`Shopify mer error`, e);
            throw e;
        }
    }

    public async clearRevenueData() {
        await this.store.clearAll();
    }

    // Method to pull the MER prediction
    public async getMerPrediction(accountId: string, day: string, progressUpdate: any) {
        try {
            console.log(`fetching new prediction for ${day}`)
            const pull = async () => {
                try {
                    return await ApiService.get(`/account-data/${accountId}/predictions/mer`, { params: { day } });
                } catch(e) {
                    throw e;
                }
            }

            let prediction: any = await pull();
            let { taskId } = prediction;

            if (taskId) {
                console.log(`Waiting for prediction job to finish...`)
                await ApiService.pollForTaskCompletion(taskId, progressUpdate);
                console.log(`Job finished, pulling data ...`)
                prediction = await pull();
                console.log(`Pulled?`, prediction)
            } else if (!prediction?.dailyMER) {
                console.error(`Prediction did not return data:`, prediction);
                return prediction;
            }

            // data returned
            console.log('UPDATING predictions-mer data source:', prediction)
            await DataService.saveDataSource('predictions-mer', prediction);
            return prediction;
        } catch(e) {
            let error = getInnerError(e);
            console.error("Error retrieving MER:", error);
            throw error;
        }
    }






    // Todo
    public tryUpdateToken = async (r, user) => {
        try {
            log('tryUpdateToken()...');
            if (!user) {
                throw "No user is available to authorize. Please logout and refresh the page.";
            }
            if (await this.isOriginalUser(r, user)) {
                log('Is original user, updating token.', user, r);
                debug(user, "Updating Shopify token.", { fbAccessToken: r.authResponse.accessToken });
                const tr = await this.getAndSaveLongToken(r.authResponse.accessToken, r.authResponse.userID);
                if (tr) {
                    // get a new user object if the original user was set on the account
                    let authed = await AuthService.checkSession() !== false;
                    if (authed) await AuthService.updateCurrentUser(user);
                    return true;
                }
            } else {
                log(`Could not update facebook, not original FB user.`);
                debug(user, "Could not update facebook, not original FB user.", { fbUserId: r?.authResponse.userID, fbAccessToken: r?.authResponse.accessToken });
            }
            return false;
        } catch(e) {
            throw e;
        }
    };

    public isOriginalUser = async (fbStatus, user) => {
        try {
            log('isOriginalUser?', fbStatus, user);

            // if no user is given, we assume original (ie. for signup page, etc):
            user = user || AuthService.getCurrentUser();
            if (!user) {
                return true;
            }

            let provider = getAccountProvider(user.activeAccount, ProviderNames.SHOPIFY);

            if (provider && fbStatus) {
                // if (provider.oauth?.accountUserId) {
                //     // if account user id matches current, it's original user.
                //     if (provider.oauth.accountUserId == fbStatus.userID) {
                //         return true;
                //     }
                // }
                return false;
            }
        } catch (e) {
            console.log('Shopify isOriginalUser error', e);
        }
        return false;
    };

}

export default new ShopifyService();